import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap, timeout } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { ConexionesService } from '../../../shared/services/conexiones.service';
import { ModulosUsuarioService } from '../../../shared/services/modulos-usuario.service';
import { TemaService } from '../../../shared/services/tema.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private baseUrl: string = '';
  /**
   * La URL del API se resuelve por el servicio de conexiones (petición externa).
   * Se publica aquí para que quien la necesite pueda esperarla: si se dispara una
   * petición antes de tenerla, la URL queda relativa (`/api/auth/...`), el dev
   * server responde 404 y el guard interpretaba "sesión inválida" → limpiaba el
   * localStorage y expulsaba al usuario al recargar.
   */
  private readonly baseUrlSubject = new BehaviorSubject<string>('');
  private readonly logoutSubject = new BehaviorSubject<boolean>(false);
  public logout$ = this.logoutSubject.asObservable();

  constructor(
    private http: HttpClient,
    private conexionesService: ConexionesService,
    private modulosUsuarioService: ModulosUsuarioService,
    private temaService: TemaService,
  ) {
    //Subscripción al cambio de conexion
    conexionesService.conexionesRO.subscribe((res) => {
      if (res && res.length > 0) {
        const apiNombre = environment.conexiones_nombre.auth;
        const conexion: any = conexionesService.buscarConexion(apiNombre);
        this.baseUrl = conexion?.URL;
        if (this.baseUrl) this.baseUrlSubject.next(this.baseUrl);
      }
    });
  }

  /** Emite la URL del API en cuanto esté disponible (una sola vez). */
  private esperarBaseUrl(): Observable<string> {
    return this.baseUrlSubject.pipe(
      filter((url: string) => !!url),
      take(1),
    );
  }

  login = (usuario: any): Observable<any> => {
    const url = `${this.baseUrl}/api/auth/login`;

    return this.http.post(url, usuario).pipe(
      timeout(30 * 1000),
      tap((res: any) => {
        if (res.msg) localStorage.setItem('token', res.msg);
        localStorage.setItem('user', JSON.stringify(res.payload));
      }),
    );
  };

  loginGoogle = (usuario: { token: string; usuario: any }): Observable<any> => {
    const url = `${this.baseUrl}/api/auth/login-google`;

    return this.http.post(url, usuario).pipe(
      timeout(30 * 1000),
      tap((res: any) => {
        if (res.msg) localStorage.setItem('token', res.msg);
        localStorage.setItem('user', JSON.stringify(res.payload));
      }),
    );
  };

  /**
   * Deja listo lo que el home necesita pintar de entrada: los módulos (de ellos
   * depende el permiso para elegir tema) y después el tema del usuario. Si los
   * módulos fallan se sigue; show-modules los reintenta y muestra el error.
   */
  prepararInicioSesion = (): Observable<void> => {
    return this.modulosUsuarioService.cargarModulosUsuario().pipe(
      catchError(() => of(null)),
      switchMap(() => this.temaService.iniciarSesion()),
    );
  };

  registroUsuario = (usuario: any): Observable<any> => {
    const url = `${this.baseUrl}/api/auth/signup`;

    return this.http.post(url, usuario).pipe(
      tap((res: any) => {
        // if(res.msg)
        //   localStorage.setItem("token",res.msg);
        //   localStorage.setItem("user",JSON.stringify(res.payload));
      }),
    );
  };

  validarToken = (): Observable<boolean> => {
    // Espera la URL del API: sin esto la petición salía relativa y devolvía 404.
    return this.esperarBaseUrl().pipe(
      switchMap((baseUrl: string) =>
        this.http
          .get(`${baseUrl}/api/auth/renewToken`, {
            headers: {
              'x-auth': localStorage.getItem('token') || '',
            },
          })
          .pipe(
            tap((res: any) => {
              if (res.msg) localStorage.setItem('token', res.msg);
            }),
            map(() => true),
            catchError((error: HttpErrorResponse) => {
              // 401/403 = sesión inválida. Un fallo de red o de configuración
              // (status 0, 404, 5xx) no debe cerrar la sesión del usuario.
              const esSesionInvalida = error?.status === 401 || error?.status === 403;
              return of(!esSesionInvalida && !!localStorage.getItem('token'));
            }),
          ),
      ),
    );
  };

  logout = () => {
    localStorage.clear();
    this.modulosUsuarioService.limpiarCache();
    this.logoutSubject.next(true);
  };
}
